import {
  useRef, useEffect, useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import * as S from './PkMovementForm.styles'
import { actions } from '../../StockMovementsState'
import { PkItem, PkMovementSchemaType } from '../../StockMovementsTypes'
import {
  Button, Typography,
} from '@mui/material'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { useSelector } from 'src/utils/store'
import { useHistory } from 'react-router-dom'
import { DivPkMovementType } from './components/DivPkMovementType'
import { PkModalGondola } from './components/PkModalGondola'
import { PkModalSurplus } from './components/PkModalSurplus'
import { PkModalReport } from './components/PkModalReport'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

enum ModalType {
  REPORT = 'report',
  GONDOLA = 'gondola',
  SURPLUS = 'surplus'
}

export const PkMovementForm: React.FC = () => {
  const stockMovements = useSelector((state) => state.stockMovements)
  const {
    items, badge, loading, validatedAddress,
  } = stockMovements
  const { fc } = useFulfillmentCenter()

  const [modalType, setModalType] = useState<ModalType>()
  const [openModal, setOpenModal] = useState(false)

  const [startedAt, setStartedAt] = useState<Date | null>(null)

  const currentItemIdx = useRef<number>(0)

  const history = useHistory()

  const {
    register,
    formState: { errors },
    getValues,
    trigger,
    setFocus,
    setError,
    setValue,
    clearErrors,
  } = useForm<PkMovementSchemaType>()
  const dispatch = useDispatch()

  const dateNow = () => {
    const date = new Date()
    date.setHours(date.getHours() - 3)
    return date
  }

  const clearState = () => {
    dispatch(actions.clearState())
    setValue('badge', '')
    setValue('withdrawAddress', '')
    setValue('inputAddress', '')
    setValue('barcode', '')
    currentItemIdx.current = 0
    clearErrors()
  }

  const showModal = (type: ModalType) => {
    setModalType(type)
    setOpenModal(true)
  }

  const reportSubmit = (reportMessage: string) => {
    setOpenModal(false)
    toast.info(reportMessage)
  }

  const batchesFits = () => {
    if (items) {
      const onSuccess = () => {
        setOpenModal(false)
        setTimeout(() => {
          toast.success(`Item '${items[currentItemIdx.current].productDescription.toUpperCase()}' movido com sucesso!`)
          const itemsToUpdate = cloneDeep(items)
          itemsToUpdate[currentItemIdx.current].done = true
          currentItemIdx.current += 1
          if (currentItemIdx.current < items.length) {
            setValue('inputAddress', '')
            setValue('barcode', '')
            dispatch(actions.updateMovementsField({ key: 'items', value: itemsToUpdate }))
            dispatch(actions.updateMovementsField({ key: 'validatedAddress', value: false }))
            setFocus('inputAddress')
            dispatch(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
          } else {
            toast.success('Todos os itens foram movidos com sucesso!')
            clearState()
          }
          setStartedAt(null)
        }, 500)
      }

      dispatch(
        actions.pkItemMovement({
          item: items[currentItemIdx.current],
          startedAt: startedAt || dateNow(),
          badge: badge || '',
          onSuccess,
        }),
      )
    }
  }

  const batchesDidNotFits = (item: PkItem) => {
    if (items) {
      const onSuccess = () => {
        setOpenModal(false)
        setTimeout(() => {
          toast.success('Rota recalculada, continue com as movimentações.')
          currentItemIdx.current = 0
          setValue('inputAddress', '')
          setValue('barcode', '')
          dispatch(actions.updateMovementsField({ key: 'validatedAddress', value: false }))
          setFocus('inputAddress')
          dispatch(actions.updateMovementsField({ key: 'pkModalDisableButtons', value: false }))
        }, 500)
      }

      dispatch(
        actions.pkItemDidNotFits({
          items,
          item,
          startedAt: startedAt || dateNow(),
          badge: badge || '',
          onSuccess,
        }),
      )
    }
  }

  const onBadgeSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('badge')

    const onSuccess = () => {
      input.blur()
      setFocus('withdrawAddress')
    }

    if (validationSuccess) {
      dispatch(
        actions.validateSupervisorBadge({
          badge: getValues('badge'),
          onSuccess,
          setError: (message) => setError('badge', { message }),
        }),
      )
    }
  }

  const onWithdrawAddressSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('withdrawAddress')

    const onSuccess = () => {
      input.blur()
      setFocus('inputAddress')
    }

    if (validationSuccess) {
      dispatch(
        actions.getPkItems({
          fcId: fc.id,
          address: getValues('withdrawAddress').trim(),
          badge: badge || '',
          onSuccess,
          setError: (message) => setError('withdrawAddress', { message }),
        }),
      )
    }
  }

  const onInputAddressSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('inputAddress')

    if (validationSuccess) {
      if (items) {
        const item = items[currentItemIdx.current]

        const onSuccess = () => {
          dispatch(actions.updateMovementsField({ key: 'validatedAddress', value: true }))
          input.blur()
          setStartedAt(dateNow())
          setFocus('barcode')
        }

        dispatch(
          actions.validateMovementAddress({
            idModel: item.idModel,
            address: item.bestPosition.address,
            addressToValidate: getValues('inputAddress').trim().toUpperCase(),
            isGondola: item.isGondola,
            onSuccess,
            setError: (message) => setError('inputAddress', { message }),
          }),
        )
      } else {
        setError('inputAddress', { message: 'Lotes não encontrados no sistema' })
      }
    }
  }

  const onBarcodeSubmit = async (input: HTMLInputElement) => {
    const validationSuccess = await trigger('barcode')

    const onSuccess = () => {
      if (items) {
        input.blur()
        showModal(items[currentItemIdx.current].isGondola ? ModalType.GONDOLA : ModalType.SURPLUS)
      }
    }

    if (validationSuccess) {
      if (items) {
        dispatch(
          actions.validatePkModelBarcode({
            barcode: getValues('barcode'),
            item: items[currentItemIdx.current],
            onSuccess,
            setError: (message) => setError('barcode', { message }),
          }),
        )
      }
    }
  }

  const fieldSubmitActions: Record<keyof PkMovementSchemaType, (input: HTMLInputElement) => void> = {
    badge: onBadgeSubmit,
    withdrawAddress: onWithdrawAddressSubmit,
    inputAddress: onInputAddressSubmit,
    barcode: onBarcodeSubmit,
  }

  const onPressEnter = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (loading) return

    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement
      fieldSubmitActions[input.name](input)
    }
  }

  useEffect(() => {
    if (!badge) {
      setFocus('badge')
    }
  }, [badge, setFocus])

  useEffect(() => {
    dispatch(actions.clearState())
  }, [dispatch])

  const pkModal = (item: PkItem) => {
    if (modalType === ModalType.REPORT) {
      return (
        <PkModalReport
          item={item}
          show={openModal}
          buttonCancelAction={() => setOpenModal(false)}
          buttonConfirmAction={(reportMessage: string) => reportSubmit(reportMessage)}
          testId='pk-movement-modal-report'
        />
      )
    } if (modalType === ModalType.GONDOLA) {
      return (
        <PkModalGondola
          item={item}
          show={openModal}
          buttonDidNotFitsAction={(_item: PkItem) => batchesDidNotFits(_item)}
          buttonFitsAction={() => batchesFits()}
          buttonCloseAction={() => setOpenModal(false)}
          testId='pk-movement-modal-gondola'
        />
      )
    } if (modalType === ModalType.SURPLUS) {
      return (
        <PkModalSurplus
          item={item}
          show={openModal}
          buttonDidNotFitsAction={(_item: PkItem) => batchesDidNotFits(_item)}
          buttonFitsAction={() => batchesFits()}
          buttonCloseAction={() => setOpenModal(false)}
          testId='pk-movement-modal-surplus'
        />
      )
    }
    return null
  }

  return (
    <S.FormContainer>
      <S.Container>
        <S.FormHeader>
          <S.FormTitle>
            <Typography variant='h6' fontWeight='bold'>Movimentação PK</Typography>
          </S.FormTitle>
        </S.FormHeader>
        <S.CustomFieldMui
          onKeyPress={onPressEnter}
          inputProps={{
            ...register('badge',
              { required: true, disabled: !!badge }),
            'data-testid': 'pk-movement-form-badge',
          }}
          disabled={!!badge}
          error={!!errors.badge}
          helperText={errors.badge?.message}
          label='replacementForm.badgelabel'
          name='badge'
          testId='pk-movement-form-badge'
          type='password'
          size='medium'
          fullWidth
          autoComplete='off'
          autoFocus
        />
        <S.CustomFieldMui
          onKeyPress={onPressEnter}
          inputProps={{
            ...register('withdrawAddress',
              { required: true, disabled: !badge || (items !== undefined && items.length > 0) }),
            'data-testid': 'pk-movement-form-withdrawAddress',
          }}
          disabled={!badge || (items !== undefined && items.length > 0)}
          hidden={!badge}
          error={!!errors.withdrawAddress}
          helperText={errors.withdrawAddress?.message}
          label='replacementForm.withdrawAddresslabel'
          name='withdrawAddress'
          size='medium'
          fullWidth
          testId='pk-movement-form-withdrawAddress'
        />
        {
          (items !== undefined && items.length > 0 && currentItemIdx.current < items.length)
          && (
          <>
            <DivPkMovementType
              isGondola={items[currentItemIdx.current].isGondola}
              subtitle={!validatedAddress}
              address={items[currentItemIdx.current].bestPosition.address}
            />
            <S.CustomFieldMui
              onKeyPress={onPressEnter}
              inputProps={{
                ...register('inputAddress',
                  { required: true, disabled: validatedAddress }),
                'data-testid': 'pk-movement-form-inputAddress',
              }}
              disabled={validatedAddress}
              error={!!errors.inputAddress}
              helperText={errors.inputAddress?.message}
              label='replacementForm.inputAddresslabel'
              placeholder='Bipe ou digite o endereço de entrada'
              name='inputAddress'
              size='medium'
              fullWidth
              testId='pk-movement-form-inputAddress'
            />
            {validatedAddress && (
              <>
                <S.ProductInfo>Bipe o produto indicado para confirmar</S.ProductInfo>
                <S.ProductInfoBox>{items[currentItemIdx.current].productDescription.toUpperCase()}</S.ProductInfoBox>
                <S.CustomFieldMui
                  onKeyPress={onPressEnter}
                  inputProps={{
                    ...register('barcode',
                      { required: true }),
                    'data-testid': 'pk-movement-form-barcode',
                  }}
                  error={!!errors.barcode}
                  helperText={errors.barcode?.message}
                  label='pkMovementForm.product.barcode'
                  name='batch'
                  size='medium'
                  fullWidth
                  testId='pk-movement-form-barcode'
                />
                {false
                && (
                <S.ReportButton
                  data-testid='pk-movement-report-button'
                  onClick={() => showModal(ModalType.REPORT)}
                />
                )}
                {pkModal(items[currentItemIdx.current])}
              </>
            )}
          </>
          )
        }
        {!badge
          ? (
            <S.ButtonSection style={{ justifyContent: 'right' }}>
              <Button onClick={() => history.push('/movements/replacement-main')} variant='contained'>Voltar</Button>
            </S.ButtonSection>
          )
          : (
            <S.ButtonSection style={{ justifyContent: 'center' }}>
              <Button onClick={clearState} variant='contained'>Limpar</Button>
            </S.ButtonSection>
          )}
      </S.Container>
    </S.FormContainer>
  )
}
